import React, { useMemo } from 'react';
// @ts-ignore
import { emojify } from 'react-emojione';
import { useIntl } from 'react-intl';
import { TMessage } from 'ui-model';

import { WithStyles, withStyles } from '@material-ui/core';
import Avatar from '@material-ui/core/Avatar';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import classnames from 'classnames';
import { get, isString, map } from 'lodash';
import moment from 'moment';
import { ChatActivityReason } from 'requests/resources';

import { RolloutEnum } from '@lib/resources/types';
import configService from '@lib/services/configService';
import storageService from '@lib/services/storageService';
import { useCurrentUser } from '@lib/hooks';

import { REF_PROVISIONING } from 'components/Chat/constants';
import usePermissions from 'hooks/usePermissions';
import IconAvatarBot from 'icons/IconAvatarBot';
import { muiThemeLuma } from 'themes';

import { MessageDetails } from '../MessageDetails';
import styles from './Message.styles';
import messages from './Message.messages';
import { parseMessageContent, replaceChatActivityReasonKeys } from './Messages.utils';
import { MessageAvatar } from '../MessageAvatar';

export interface MessageProps extends WithStyles<typeof styles> {
  message: TMessage;
  chatActivityReasons?: ChatActivityReason[];
  retriedChannels: { name: string; reason: string }[];
}

const Message: React.FC<MessageProps> = ({ classes, message, chatActivityReasons, retriedChannels }) => {
  const viewer = useCurrentUser();
  const { formatMessage } = useIntl();
  const { hasAccessToProduct } = usePermissions();

  // WORKAROUND Switch type in case it's the patient instead of staff user the one receiving messages
  const isOutbound = message.type === 'outbound';

  const interlocutor = useMemo(() => {
    if (message.type === 'inbound') {
      return message.patient;
    }
    return message.createdBy;
  }, [message]);

  let text = message.text || '';

  // Replace ChatActivity close reason IDs by their text
  text = replaceChatActivityReasonKeys(text, chatActivityReasons);

  let messageHtml = text || '';

  if (message.type === 'outbound' || message.ref === 'chat') {
    // Links & URLs clickable for `internal` messages only
    const disableLinks = !hasAccessToProduct(RolloutEnum.ChatInternalLinks) || message.visibility !== 'internal';
    messageHtml = parseMessageContent(messageHtml, disableLinks);

    if (message.mentions && message.mentions.length > 0) {
      const mentionsPattern = new RegExp(`\\B(${map(message.mentions, 'mention').join('|')})\\b`, 'g');

      messageHtml = messageHtml.replace(
        mentionsPattern,
        (mention) =>
          `<span style="background-color: #fff; border-radius: 12px; color: #3460C5; font-weight: inherit; padding: 0px 4px; white-space: normal">
            ${mention}
          </span>`,
      );
    }

    // highlight templates
    messageHtml = messageHtml.replace(
      /({{[^}]+}})/gm,
      `<span style="background-color: #f1f1f1; border-radius: 4px; color: ${muiThemeLuma.custom.palette.colorDarkCyan}; font-weight: 400;">$1</span>`,
    );
  }

  // Respect provisioning ON + Setting is turned ON:
  // Users and providers who are provisioned for Hub will only see messages in Hub for other products/features they are provisioned for.
  if (
    get(viewer, 'salesforceData.respectProvisioning', false) &&
    get(viewer, 'setting.settings.communication.suppressMessagesByProvisioning', false) &&
    message.ref &&
    !hasAccessToProduct(get(get(REF_PROVISIONING, message.ref), 'provisioning'))
  ) {
    messageHtml = formatMessage(messages.messageSuppresed);
  }

  messageHtml = messageHtml.replace(
    /(:[\w]+:)/gm,
    (match) => `<span style="font-size: 14px;">${emojify(match, { output: 'unicode' })}</span>`,
  );

  return (
    <div className={classes.root}>
      {message.ref === 'chatAuditTz' ? (
        <Typography
          className={classnames(classes.wrapper, classes.chatAudit)}
          component="div"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: `${text} — ${moment((message.createdAt as unknown) as string).format('lll')}`,
          }}
        />
      ) : (
        <div className={classnames(classes.wrapper, { [classes.outbound]: isOutbound })}>
          {!isOutbound && interlocutor && (
            <div className={classes.pullBottom}>
              <MessageAvatar interlocutor={interlocutor} size="xsmall" />
            </div>
          )}
          <div
            className={classnames(classes.messageWrapper, {
              [classes.outbound]: isOutbound,
              [classes.internal]: message.visibility === 'internal',
            })}
          >
            <div
              className={classes.messageContent}
              dangerouslySetInnerHTML={{ __html: messageHtml }} // eslint-disable-line react/no-danger
            />
            <MessageDetails
              retriedChannels={retriedChannels}
              message={message}
              variant="captionBold"
              color={isOutbound && message.visibility !== 'internal' ? 'white' : 'primary'}
            />
            {message.file && (
              <Button
                classes={{ root: classes.attachButton, label: classes.attachButtonLabel }}
                disabled={!isString(message.file)}
                href={`${configService.getApiBasePath()}/fileUploads/${
                  message.file
                }/file?token=${storageService.getToken()}`}
                target="blank"
                variant="contained"
              >
                <AttachFileIcon className={classes.attachIcon} />
                {!isString(message.file) ? formatMessage(messages.fileUploading) : formatMessage(messages.fileAttached)}
              </Button>
            )}
          </div>
          {isOutbound && (
            <div className={classes.pullBottom}>
              {message.automated || !interlocutor ? (
                <Avatar className={classes.avatarBot}>
                  <IconAvatarBot fontSize="small" />
                </Avatar>
              ) : (
                <MessageAvatar interlocutor={interlocutor} size="xsmall" />
              )}
            </div>
          )}
        </div>
      )}
      {isOutbound && message.ref !== 'chatAuditTz' && interlocutor && (
        <Typography className={classes.interlocutor}>
          {message.automated ? formatMessage(messages.viaLuma) : interlocutor.name}
        </Typography>
      )}
    </div>
  );
};

export default withStyles(styles)(Message);
